<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-10 h-screen w-screen overflow-hidden bg-neutral-500/50 transition-opacity"
  />
  <transition
    enter-active-class="transition duration-500 ease-in-out"
    leave-active-class="transition duration-500 ease-in-out"
    enter-from-class="-translate-x-full"
    enter-to-class="translate-x-0"
    leave-from-class="translate-x-0"
    leave-to-class="-translate-x-full"
  >
    <SfDrawer
      ref="navigationSidebar"
      :model-value="isOpen"
      placement="left"
      class="right-[50px] z-20 w-full overflow-y-auto bg-white tablet:w-3/4 desktop:hidden"
    >
      <nav>
        <div class="mx-4 flex items-center justify-between border-b py-4">
          <div class="flex items-center font-medium">
            <SfIconArrowBack
              v-if="currentCategory.name"
              class="fill-secondary text-neutral-500"
              tabindex="0"
              @click="goBack()"
              @keydown.enter=" goBack()"
            />
            <p class="ml-5 font-medium">
              {{ currentCategory.name }}
            </p>
          </div>
          <SfButton
            variant="tertiary"
            square
            aria-label="Close menu"
            class="ml-2"
            @click="close()"
          >
            <SfIconClose class="fill-neutral-500 text-neutral-500" />
          </SfButton>
        </div>
        <ul class="mb-6 mt-2">
          <SfListItem
            v-if="currentCategory.name"
            size="lg"
            tag="button"
            type="button"
          >
            <div
              class="flex items-center justify-between text-primary-700"
              @click="navigateToPage(currentCategory)"
            >
              {{ $t('Show all') }}
              <SfIconChevronRight class="text-neutral-500" />
            </div>
          </SfListItem>
          <template
            v-for="(category, key) in currentCategory.childrenItems"
            :key="key"
          >
            <SfListItem
              size="lg"
              tag="button"
              type="button"
              @click="handleCategoryClick(category)"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <p class="text-left">
                    {{ category.name }}
                  </p>
                </div>
                <SfIconChevronRight class="text-neutral-500" />
              </div>
            </SfListItem>
          </template>
          <SfListItem
            size="lg"
            tag="button"
            type="button"
          >
            <div
              class="flex items-center justify-between text-primary-700"
              @click="navigateToPage(currentCategory, undefined, 'new')"
            >
              {{ $t('New') }}
              <SfIconChevronRight class="text-neutral-500" />
            </div>
          </SfListItem>
          <SfListItem
            size="lg"
            tag="button"
            type="button"
          >
            <div
              class="flex items-center justify-between text-primary-700"
              @click="navigateToPage(currentCategory, undefined, 'sale')"
            >
              {{ $t('Sale') }}
              <SfIconChevronRight class="text-neutral-500" />
            </div>
          </SfListItem>
          <SfListItem
            v-if="currentCategory.name"
            size="lg"
            tag="button"
            type="button"
          >
            <NuxtLink
              class="flex items-center justify-between text-primary-700"
              :to="`/c/${getSlug(currentCategory)}?filters=mykik`"
              @click.native="close()"
            >
              {{ $t('myKiK') }}
              <SfIconChevronRight class="text-neutral-500" />
            </NuxtLink>
          </SfListItem>
          <template v-if="categoryConfiguration.topLevelLinks">
            <SharedLink
              v-for="(topLevelLink, index) in categoryConfiguration.topLevelLinks"
              :key="index"
              :link="topLevelLink"
              :data-type="topLevelLink.type"
              class="!bg-transparent p-2 text-sm !text-neutral-700 hover:!text-neutral-950 active:!text-neutral-950"
              :class="{ 'mx-md my-4 min-w-[56px] max-w-[56px] cursor-pointer border-b-0 !p-0': topLevelLink.mode?.type === 'image' }"
              @mouseover="close()"
            >
              <span v-if="topLevelLink.mode?.type === 'title'">
                {{ topLevelLink.mode.title }}
              </span>
              <NuxtImg
                v-if="topLevelLink.mode?.type === 'image' && topLevelLink.mode?.src"
                :src="topLevelLink.mode?.src"
                :placeholder="topLevelLink.mode?.src"
                :alt="topLevelLink.mode?.alt"
                :title="topLevelLink.mode?.title"
                loading="lazy"
              />
            </SharedLink>
          </template>
          <ServiceLinks
            @close="close"
          />
          <template v-if="getAdditionalLinks(currentCategory)">
            <SfListItem
              v-for="magnoliaLink in getAdditionalLinks(currentCategory)"
              :key="magnoliaLink.url"
              size="lg"
              tag="button"
              type="button"
            >
              <SharedLink
                :link="magnoliaLink"
                @click="close()"
              >
                {{ magnoliaLink.name }}
              </SharedLink>
            </SfListItem>
          </template>
        </ul>
        <template v-if="getImages(currentCategory)">
          <div
            v-for="image in getImages(currentCategory)"
            :key="image.alt"
            class="mt-0.5 border-neutral-300 bg-neutral-100 md:rounded-md"
          >
            <SharedLink :link="image.to">
              <NuxtImg
                :data-type="image.to.type"
                :alt="image.alt"
                :title="image.alt"
                :src="image.url"
                loading="lazy"
                class="w-full object-contain"
              />
            </SharedLink>
            <p class="my-4 px-4 text-center font-medium">
              {{ image.to.name }}
            </p>
          </div>
        </template>
      </nav>
    </SfDrawer>
  </transition>
</template>

<script lang="ts" setup>
import {
  SfIconClose,
  SfButton,
  SfDrawer,
  SfListItem,
  SfIconChevronRight,
  SfIconArrowBack,
  useTrapFocus,
} from '@storefront-ui/vue';
import { onClickOutside } from '@vueuse/core';
import ServiceLinks from './ServiceLinks.vue';
import type { Category } from '~/types/GraphQL';
import { urlGetters } from '~/composables/getters';

interface Props {
  isOpen: boolean;
  categorySlug: string;
  categories?: any[];
  additionalInfo: any;
  categoryConfiguration?: any;
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  categories: () => [],
  additionalInfo: () => ({}),
  categoryConfiguration: undefined,
});

const emit = defineEmits({
  // eslint-disable-next-line vue/return-in-emits-validator
  close: () => {},
});

const history = ref([]);

const { $i18n } = useNuxtApp();

const currentCategory = ref({ childrenItems: props.categories } as Category);

const navigateToPage = (category: Category, subcategory?: Category, newOrSale?: 'new' | 'sale') => {
  const navigationUrl = urlGetters.getCategoryNavigationByType(newOrSale, $i18n);
  let page = `${navigationUrl}`;
  if (getSlug(category)) {
    page = `${page}/${getSlug(category)}`;
  }

  if (subcategory) {
    page = `${page}/${getSlug(subcategory)}`;
  }

  navigateTo(page);
  close();
};

const handleCategoryClick = (category: Category) => {
  history.value.push(currentCategory.value);
  if (category.childrenItems?.length) {
    currentCategory.value = category;
  }
  else {
    navigateToPage(category);
  }
};

const close = () => {
  currentCategory.value = { childrenItems: props.categories } as Category;
  emit('close');
};

const goBack = () => {
  currentCategory.value = history.value[history.value.length - 1];
  history.value = history.value.slice(0, history.value.length - 1);
};

const navigationSidebar = ref();
useTrapFocus(navigationSidebar, { activeState: props.isOpen });

onClickOutside(navigationSidebar, () => {
  close();
});

const getSlug = (category: Category) => (category.slug ?? category?.name?.toLowerCase());
const getImages = (category: Category) => props.additionalInfo?.[category.id]?.images;
const getAdditionalLinks = (category: Category) => props.additionalInfo?.[category.id]?.links;
</script>
